/* global forte */
import { clients } from '../clients';

const { backendRest } = clients.direct;

// Forte returns the literal string "null" for some card metadata fields
// (card_brand / card_brand_type / prepaid_ind). Treat that as empty.
const emptyIfNull = (value) => (value == null || value === 'null' ? '' : value);

/**
 * Dynamically inject the Forte.js script (mirrors Payrix's loadPayFieldsScript).
 * Loads a single script from the config-driven CDN url (sandbox value is
 * https://sandbox.forte.net/api/js/v1 — never hardcoded here).
 * Guards against double-injection: resolves immediately if Forte.js is present.
 * @param {string} forte2CdnUrl Forte.js url from paymentConfig
 * @returns {Promise<void>}
 */
export const loadForte2Script = (forte2CdnUrl) => new Promise((resolve, reject) => {
  if (!forte2CdnUrl) {
    reject(new Error('Forte2 CDN URL is not configured.'));
    return;
  }
  if (typeof forte !== 'undefined') {
    resolve();
    return;
  }
  if (document.querySelector(`script[src="${forte2CdnUrl}"]`)) {
    resolve();
    return;
  }
  const s = document.createElement('script');
  s.src = forte2CdnUrl;
  s.onload = resolve;
  s.onerror = () => reject(new Error('Failed to load Forte.js'));
  document.head.appendChild(s);
});

/**
 * Tokenize a card via the Forte.js global. Resolves with the FULL response
 * object. The one-time token lives on `response.onetime_token` (NO underscore —
 * confirmed in sandbox; Forte's field-reference doc wrongly calls it
 * one_time_token). `bin_attribute: "true"` returns card_category inline, so no
 * separate BIN-lookup call is needed.
 * @returns {Promise<object>} full Forte.js response (with normalized card_* fields)
 */
export const createForte2Token = ({
  apiLoginId, cardNumber, expireMonth, expireYear, cvv,
}) => new Promise((resolve, reject) => {
  forte.createToken({
    api_login_id: apiLoginId,
    card_number: cardNumber,
    expire_month: expireMonth,
    expire_year: expireYear,
    cvv,
    bin_attribute: 'true', // returns card_category inline, no auth
  })
    .success((response) => {
      resolve({
        ...response,
        // surfaced explicitly for callers
        onetime_token: response.onetime_token,
        card_category: response.card_category, // 'C' = credit, 'D' = debit
        card_type: response.card_type, // brand: visa / mast / amex / disc
        // string "null" sanitized to empty
        card_brand: emptyIfNull(response.card_brand),
        card_brand_type: emptyIfNull(response.card_brand_type),
        prepaid_ind: emptyIfNull(response.prepaid_ind),
      });
    })
    .error((response) => {
      // Forte.js invokes this callback; guard against a null/description-less response so the
      // reject carries a usable message instead of throwing here (which would hang the awaiting
      // caller and leave the verify button stuck) or surfacing "undefined" to the clerk.
      const message = (response && response.response_description)
        || 'Card could not be tokenized. Please verify the card details and try again.';
      reject(new Error(message));
    });
});

/**
 * Tokenize a bank account via the Forte.js global (eCheck sibling of
 * createForte2Token). An eCheck token response carries NO card metadata
 * (card_category / card_type / card_brand), so none are referenced here and
 * bin_attribute is omitted. accountType is 'c' (checking) or 's' (savings).
 * @returns {Promise<object>} { onetime_token, last_4, account_type }
 */
export const createForte2EcheckToken = ({
  apiLoginId, accountNumber, routingNumber, accountType,
}) => new Promise((resolve, reject) => {
  forte.createToken({
    api_login_id: apiLoginId,
    account_number: accountNumber,
    routing_number: routingNumber,
    account_type: accountType,
  })
    .success((response) => {
      resolve({
        // surfaced explicitly for callers
        onetime_token: response.onetime_token,
        last_4: response.last_4,
        account_type: response.account_type,
      });
    })
    .error((response) => {
      // Same guard as the card path: a null/description-less response must not throw inside this
      // callback (which would hang the awaiting caller) or surface "undefined" to the clerk.
      const message = (response && response.response_description)
        || 'Bank account could not be tokenized. Please verify the account details and try again.';
      reject(new Error(message));
    });
});

/**
 * Compute the surcharge fee for a Forte2 payment. Emitted fee/total are DECIMAL
 * DOLLARS (e.g. 1177.86) — Forte has no cents wire format. Rates arrive as
 * strings from paymentConfig, so parseFloat defensively.
 *
 * The rounding is done in INTEGER CENTS against INTEGER BASIS POINTS, not float
 * dollars. Forte validates service_fee_amount against the location's rate plan
 * with EXACT half-up rounding and ZERO tolerance: a fee one cent off is a hard
 * U28 CONV FEE INCORRECT decline. Float math (sub * rate / 100) can land a hair
 * below an exact half-cent and round DOWN where Forte rounds UP (e.g. 40.20 @
 * 2.5% = 1.005 -> Forte 1.01, old float path 1.00), so the taxpayer could never
 * pay that amount by card. subCents * rateBp is integer*integer (exact to ~$9B)
 * and the quotient is only ever divided by a power of ten, so no value lands
 * between two representable cents; JS Math.round breaks ties toward +Infinity,
 * which for positive amounts is identical to Forte's half-away-from-zero.
 * @returns {{ fee: number, total: number }} both 2-decimal dollar numbers
 */
export const computeForte2Fee = ({
  cardCategory, subtotal, creditPercentage, debitPercentage,
}) => {
  const sub = parseFloat(subtotal) || 0;
  const creditRate = parseFloat(creditPercentage) || 0;
  const debitRate = parseFloat(debitPercentage) || 0;
  const rate = cardCategory === 'D' ? debitRate : creditRate;
  const subCents = Math.round(sub * 100);
  const rateBp = Math.round(rate * 100); // 2.5 -> 250, 1.95 -> 195
  const feeCents = Math.round((subCents * rateBp) / 10000);
  return { fee: feeCents / 100, total: (subCents + feeCents) / 100 };
};

/**
 * Compute the eCheck quote for a Forte2 payment. Unlike the card fee, the eCheck
 * service fee is a FLAT dollar amount from the location rate plan
 * (services.echeck.service_fee_amount, e.g. $1.50) — NOT a percentage — so it is
 * known on dialog open with no BIN lookup and no credit/debit ambiguity.
 *
 * Kept separate from computeForte2Fee on purpose: that function's signature and
 * percentage semantics were verified E2E and must not absorb a flat-fee concern.
 * Integer cents for the same reason as the card path — Forte validates the fee
 * with zero tolerance, and float addition of two decimal dollars is not reliably
 * exact. Emitted fee/total are decimal dollars; cents are internal to the math.
 * A missing/empty echeckFee resolves to 0 via `parseFloat(x) || 0`, so configs
 * with no eCheck fee behave exactly as before (fee 0, total === subtotal).
 * @returns {{ fee: number, total: number }} both 2-decimal dollar numbers
 */
export const computeForte2EcheckQuote = ({ subtotal, echeckFee }) => {
  const subCents = Math.round((parseFloat(subtotal) || 0) * 100);
  const feeCents = Math.round((parseFloat(echeckFee) || 0) * 100);
  return { fee: feeCents / 100, total: (subCents + feeCents) / 100 };
};

/**
 * POST the tokenized payment to our backend (mirrors how payrix.util posts via
 * backendRest). Amounts are decimal dollars. authorization_amount is computed
 * HERE as subtotal + serviceFeeAmount — Forte requires the authorized amount to
 * include the service fee, and summing inside the util means no caller can get
 * that invariant wrong. On the card path, payload.cardType (the raw Forte
 * card_type brand from createForte2Token) is forwarded as card_type so the
 * backend can attach it to the sale's card object. The caller branches on
 * data.response.response_type (A/D/E), so declines are NOT thrown here — only
 * transport/server errors reject.
 * @returns {Promise<object>} backend response data
 */
export const processForte2Payment = async (payload) => {
  const {
    onetimeToken,
    subtotal, // decimal dollars
    serviceFeeAmount = 0,
    firstName,
    lastName,
    referenceId,
    phone,
    email,
    company,
    paymentConfig,
    // card-only (undefined on the eCheck path -> conditional field below is omitted)
    cardType,
    // eCheck-only (undefined on the card path -> conditional fields below are omitted)
    paymentType,
    accountType,
    accountHolder,
  } = payload;

  // Integer-cents assembly so the backend can reconstruct the subtotal by
  // subtraction the way Forte does: authorization_amount - service_fee_amount
  // must reproduce the charged subtotal EXACTLY (the backend is about to
  // validate that invariant). Summing the two cent integers before the single
  // /100 keeps that difference bit-identical to what was quoted and charged.
  const sub = parseFloat(subtotal) || 0;
  const fee = parseFloat(serviceFeeAmount) || 0;
  const authorizationAmount = (Math.round(sub * 100) + Math.round(fee * 100)) / 100;

  // client-minted, unique per attempt (mirrors Payrix's payrix-${Date.now()}-${rand})
  const requestId = `forte2-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

  const requestBody = {
    onetime_token: onetimeToken,
    authorization_amount: authorizationAmount,
    ...(fee > 0 && { service_fee_amount: fee }),
    billing_address: {
      first_name: firstName,
      last_name: lastName,
    },
    reference_id: referenceId,
    requestId,
    phone,
    email,
    company,
    // Raw Forte card_type brand (visa/mast/amex/disc) from the tokenize response,
    // forwarded so the backend can attach it to the sale's card object. Undefined
    // on the eCheck path -> this spread contributes nothing.
    ...(cardType ? { card_type: cardType } : {}),
    // eCheck sale adds the tender + bank fields; card path leaves paymentType
    // undefined so this spread contributes nothing and the body is unchanged.
    ...(paymentType === 'ECHECK' ? {
      paymentType: 'ECHECK',
      account_type: accountType,
      account_holder: accountHolder,
    } : {}),
  };

  const body = {
    requestBody,
    baseUrl: paymentConfig.forte2BaseUrl,
    orgId: paymentConfig.forte2OrgId,
    // Partner org for the X-Forte-Auth-Organization-Id header, distinct from the
    // merchant orgId that addresses the URI. Undefined on configs that predate the
    // field — the backend falls back to orgId, so this stays optional end to end.
    authOrgId: paymentConfig.forte2AuthOrgId,
    locId: paymentConfig.forte2LocId,
    apiAccessId: paymentConfig.forte2ApiAccessId,
    apiSecureKey: paymentConfig.forte2ApiSecureKey,
  };

  try {
    const forte2Response = await backendRest.post('/tax/ccPayments/forte2/process', body);
    return forte2Response.data;
  } catch (error) {
    console.error('Error processing Forte2 payment: ', error);
    throw error;
  }
};

/**
 * POST the tokenized eCheck to our backend's Forte2 verify endpoint. The endpoint
 * replies HTTP 200 for approve/decline/error alike, so the caller branches on
 * data.response.response_type (A/D/E) and reads data.echeck.issuer_bank /
 * data.response.preauth_result on success. The caller MUST pass authorizationAmount
 * > 0. Reuses the SAME paymentConfig.forte2* keys the process util sends. sec_code /
 * reference_id / service_fee_amount are intentionally omitted per the verify contract
 * @returns {Promise<object>} backend response data
 */
export const callForte2Verify = async (payload) => {
  const {
    onetimeToken, authorizationAmount, accountType, accountHolder,
    firstName, lastName, paymentConfig,
  } = payload;

  // client-minted, unique per attempt (mirrors processForte2Payment) for log correlation
  const requestId = `forte2-verify-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

  const requestBody = {
    onetime_token: onetimeToken,
    authorization_amount: authorizationAmount, // caller MUST pass > 0
    account_type: accountType, // 'c' or 's'
    account_holder: accountHolder,
    billing_address: { first_name: firstName, last_name: lastName },
    requestId,
    // NO sec_code, NO reference_id, NO service_fee_amount
  };

  const body = {
    requestBody,
    baseUrl: paymentConfig.forte2BaseUrl,
    orgId: paymentConfig.forte2OrgId,
    // Partner org for the X-Forte-Auth-Organization-Id header, distinct from the
    // merchant orgId that addresses the URI. Undefined on configs that predate the
    // field — the backend falls back to orgId, so this stays optional end to end.
    authOrgId: paymentConfig.forte2AuthOrgId,
    locId: paymentConfig.forte2LocId,
    apiAccessId: paymentConfig.forte2ApiAccessId,
    apiSecureKey: paymentConfig.forte2ApiSecureKey,
  };

  const res = await backendRest.post('/tax/ccPayments/forte2/verify', body);
  return res.data; // caller branches on data.response.response_type
};

/**
 * Map the Forte card_type brand string to a backend TaxCCSpecialPaymentTypeEnum KEY.
 * GraphQL requires the enum KEY (e.g. VISA_FORTE2), not the lowercase value.
 * @param {string} cardType raw card_type from createToken response
 * @returns {string} enum key
 */
export function mapForte2CardTypeToSpecialPaymentTypeKey(cardType) {
  const t = String(cardType || '').trim().toLowerCase();
  switch (t) {
    case 'visa': return 'VISA_FORTE2';
    case 'mast': return 'MASTERCARD_FORTE2';
    case 'amex': return 'AMERICANEXPRESS_FORTE2';
    case 'disc': return 'DISCOVER_FORTE2';
    default: return 'OTHER_FORTE2';
  }
}

export default processForte2Payment;
